import React from "react";
import { useReveal } from "../hooks/useReveal";

/* ─────────────────────────────────────────────────────────────────
 * "More than performance" — the build/ritual section.
 * Each pillar ships a tiny on-brand animated diagram (top-right).
 * Idle: subtle ambient motion. Hover: amplifies + brightens.
 * ────────────────────────────────────────────────────────────────*/

const STROKE = "#3FE2FF";
const SOFT = "rgba(255,255,255,0.18)";

/* 01 — Chew resistance gauge sweeping toward LOCK */
function GaugeDiagram() {
  return (
    <svg viewBox="0 0 120 80" className="w-full h-full">
      <defs>
        <linearGradient id="gg" x1="0" x2="1">
          <stop offset="0" stopColor="#1FB3FF" />
          <stop offset="1" stopColor="#3FE2FF" />
        </linearGradient>
      </defs>
      {/* arc track */}
      <path d="M15 60 A45 45 0 0 1 105 60" stroke={SOFT} strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* arc fill (animated) */}
      <path d="M15 60 A45 45 0 0 1 105 60" stroke="url(#gg)" strokeWidth="2.5" fill="none" strokeLinecap="round"
        strokeDasharray="142" strokeDashoffset="142" className="origin-center">
        <animate attributeName="stroke-dashoffset" values="142;28;48;28" dur="3.6s" repeatCount="indefinite" />
      </path>
      {/* tick marks */}
      {[0, 30, 60, 90, 120, 150, 180].map((deg, i) => {
        const r = (deg * Math.PI) / 180;
        const x1 = 60 - 45 * Math.cos(r);
        const y1 = 60 - 45 * Math.sin(r);
        const x2 = 60 - 39 * Math.cos(r);
        const y2 = 60 - 39 * Math.sin(r);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={SOFT} strokeWidth="1" />;
      })}
      {/* needle */}
      <g style={{ transformOrigin: "60px 60px" }}>
        <line x1="60" y1="60" x2="60" y2="22" stroke={STROKE} strokeWidth="1.8" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" values="-70 60 60; 55 60 60; 25 60 60; 55 60 60"
            dur="3.6s" repeatCount="indefinite" />
        </line>
        <circle cx="60" cy="60" r="3" fill={STROKE} />
      </g>
      <text x="60" y="76" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="7"
        fill="rgba(255,255,255,0.45)" letterSpacing="2">LOCK</text>
    </svg>
  );
}

/* 02 — Saliva droplet rippling on the xylitol shell */
function DropletDiagram() {
  return (
    <svg viewBox="0 0 120 80" className="w-full h-full">
      {/* surface line */}
      <line x1="10" y1="60" x2="110" y2="60" stroke={SOFT} strokeWidth="1" />
      {/* expanding ripples */}
      {[0, 0.6, 1.2].map((delay, i) => (
        <ellipse key={i} cx="60" cy="60" rx="6" ry="2" fill="none" stroke={STROKE} strokeWidth="1">
          <animate attributeName="rx" values="6;48" dur="2.4s" begin={`${delay}s`} repeatCount="indefinite" />
          <animate attributeName="ry" values="2;6" dur="2.4s" begin={`${delay}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.9;0" dur="2.4s" begin={`${delay}s`} repeatCount="indefinite" />
        </ellipse>
      ))}
      {/* droplet */}
      <path d="M60 18 C 52 32, 50 44, 60 50 C 70 44, 68 32, 60 18 Z" fill={STROKE} opacity="0.9">
        <animate attributeName="opacity" values="0.55;1;0.55" dur="2.4s" repeatCount="indefinite" />
        <animateTransform attributeName="transform" type="translate" values="0 0; 0 6; 0 0" dur="2.4s" repeatCount="indefinite" />
      </path>
      {/* shimmer dot */}
      <circle cx="57" cy="28" r="1.4" fill="#fff" opacity="0.85" />
    </svg>
  );
}

/* 03 — Layered core cross-section */
function CoreDiagram() {
  return (
    <svg viewBox="0 0 120 80" className="w-full h-full">
      <defs>
        <radialGradient id="cc" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="#3FE2FF" stopOpacity="0.95" />
          <stop offset="0.7" stopColor="#1FB3FF" stopOpacity="0.3" />
          <stop offset="1" stopColor="#1FB3FF" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* outer shell */}
      <circle cx="60" cy="40" r="30" fill="none" stroke={SOFT} strokeWidth="1.2" strokeDasharray="2 4">
        <animateTransform attributeName="transform" type="rotate" from="0 60 40" to="360 60 40" dur="22s" repeatCount="indefinite" />
      </circle>
      {/* middle layer */}
      <circle cx="60" cy="40" r="22" fill="none" stroke={STROKE} strokeWidth="1" opacity="0.7" />
      {/* inner glow core */}
      <circle cx="60" cy="40" r="14" fill="url(#cc)">
        <animate attributeName="r" values="12;15;12" dur="3.2s" repeatCount="indefinite" />
      </circle>
      <circle cx="60" cy="40" r="5" fill={STROKE}>
        <animate attributeName="opacity" values="0.7;1;0.7" dur="1.8s" repeatCount="indefinite" />
      </circle>
      {/* cross-section ticks */}
      <line x1="20" y1="40" x2="26" y2="40" stroke={SOFT} />
      <line x1="94" y1="40" x2="100" y2="40" stroke={SOFT} />
    </svg>
  );
}

/* 04 — Prebiotic fiber strands weaving */
function FiberDiagram() {
  return (
    <svg viewBox="0 0 120 80" className="w-full h-full">
      {[0, 1, 2].map((i) => {
        const phase = i * 0.4;
        return (
          <path
            key={i}
            d={`M10 ${30 + i * 10} Q 40 ${10 + i * 10}, 60 ${40} T 110 ${30 + i * 10}`}
            fill="none"
            stroke={STROKE}
            strokeWidth="1.2"
            opacity={0.4 + i * 0.18}
            strokeLinecap="round"
          >
            <animate
              attributeName="d"
              values={`M10 ${30 + i * 10} Q 40 ${10 + i * 10}, 60 ${40} T 110 ${30 + i * 10};
                      M10 ${30 + i * 10} Q 40 ${70 - i * 10}, 60 ${40} T 110 ${30 + i * 10};
                      M10 ${30 + i * 10} Q 40 ${10 + i * 10}, 60 ${40} T 110 ${30 + i * 10}`}
              dur="4s"
              begin={`${phase}s`}
              repeatCount="indefinite"
            />
          </path>
        );
      })}
      {/* moving dots */}
      {[0, 1.3, 2.6].map((d, i) => (
        <circle key={i} r="2" fill={STROKE}>
          <animateMotion dur="4s" begin={`${d}s`} repeatCount="indefinite" path="M10 40 Q 40 20, 60 40 T 110 40" />
        </circle>
      ))}
    </svg>
  );
}

/* 05 — Long-form sustained pulse */
function PulseDiagram() {
  // baseline ECG-like pulse that holds long flat segments
  const d = "M0 40 L24 40 L30 22 L36 58 L42 30 L48 40 L120 40";
  return (
    <svg viewBox="0 0 120 80" className="w-full h-full">
      <line x1="0" y1="40" x2="120" y2="40" stroke={SOFT} strokeWidth="0.5" />
      <path d={d} fill="none" stroke={STROKE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
        strokeDasharray="200" strokeDashoffset="200">
        <animate attributeName="stroke-dashoffset" values="200;0;-200" dur="4.2s" repeatCount="indefinite" />
      </path>
      {/* trailing dot */}
      <circle r="2.4" fill={STROKE}>
        <animateMotion dur="4.2s" repeatCount="indefinite" path={d} />
        <animate attributeName="opacity" values="0;1;0" dur="4.2s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

/* 06 — Ritual loop (cyclical arrows) */
function LoopDiagram() {
  return (
    <svg viewBox="0 0 120 80" className="w-full h-full">
      <g style={{ transformOrigin: "60px 40px" }}>
        <animateTransform attributeName="transform" type="rotate" from="0 60 40" to="360 60 40" dur="8s" repeatCount="indefinite" />
        <circle cx="60" cy="40" r="24" fill="none" stroke={SOFT} strokeWidth="1" strokeDasharray="2 6" />
        <path d="M60 16 A24 24 0 0 1 84 40" fill="none" stroke={STROKE} strokeWidth="1.8" strokeLinecap="round" />
        <polygon points="84,38 88,40 84,42" fill={STROKE} />
        <path d="M60 64 A24 24 0 0 1 36 40" fill="none" stroke={STROKE} strokeWidth="1.8" strokeLinecap="round" />
        <polygon points="36,42 32,40 36,38" fill={STROKE} />
      </g>
      {/* center dot */}
      <circle cx="60" cy="40" r="3" fill={STROKE}>
        <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

const LAYERS = [
  {
    n: "01",
    title: "Engineered Resistance",
    body: "Calibrated chew. Sustained engagement. A grip your jaw can feel.",
    Diagram: GaugeDiagram,
  },
  {
    n: "02",
    title: "Xylitol Shell",
    body: "Sugar-free, tooth-friendly, saliva-supportive. Built to be worn daily.",
    Diagram: DropletDiagram,
  },
  {
    n: "03",
    title: "Center-Fill Core",
    body: "A custom formulation in development for the Protos experience alone.",
    Diagram: CoreDiagram,
  },
  {
    n: "04",
    title: "Prebiotic Fiber",
    body: "Wellness layered inside the core. Quiet, useful, intentional.",
    Diagram: FiberDiagram,
  },
  {
    n: "05",
    title: "Long-Form Flavor",
    body: "A sensory anchor that holds. Keeps you with the task in front of you.",
    Diagram: PulseDiagram,
  },
  {
    n: "06",
    title: "Made for a Ritual",
    body: "Not a stimulant. A modern performance ritual you can repeat.",
    Diagram: LoopDiagram,
  },
];

export default function BeyondPerformance() {
  const head = useReveal();
  return (
    <section
      id="beyond"
      data-testid="beyond-section"
      className="section relative overflow-hidden bg-[#020305]"
    >
      <div
        aria-hidden
        className="absolute -top-32 -left-24 w-[40%] aspect-square rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(31,179,255,0.45), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-24 w-[45%] aspect-square rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(63,226,255,0.4), transparent 60%)",
        }}
      />

      <div className="container-x relative z-10">
        <div ref={head} className="reveal max-w-3xl mb-12 lg:mb-16">
          <span className="font-mono text-sm uppercase tracking-[0.3em] text-white/55">
            / 05 — The Build
          </span>
          <h2
            className="kinetic mt-6"
            style={{ fontSize: "clamp(2.6rem, 6vw, 5.4rem)", lineHeight: 0.95 }}
          >
            <span className="gradient-text">MORE THAN</span>
            <br />
            <span className="outline-text-blue">PERFORMANCE.</span>
          </h2>
          <p className="mt-7 text-white/70 text-lg lg:text-xl leading-snug max-w-2xl">
            Protos isn&apos;t a functional gum.{" "}
            <span className="text-white">It&apos;s a ritual you can repeat.</span>
          </p>
          <p className="mt-4 text-white/55 text-base lg:text-lg leading-relaxed max-w-2xl">
            Every layer of the experience has been intentionally engineered to
            support focus, presence, and composure when it matters most.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden">
          {LAYERS.map((l, i) => (
            <Layer key={i} l={l} i={i} />
          ))}
        </div>

        <div className="mt-12 lg:mt-16 flex flex-col sm:flex-row sm:items-baseline gap-4">
          <p
            className="kinetic"
            style={{ fontSize: "clamp(1.6rem, 3.2vw, 2.6rem)", lineHeight: 1 }}
          >
            <span className="outline-text-blue">Not a stimulant.</span>
          </p>
          <p
            className="kinetic"
            style={{ fontSize: "clamp(1.6rem, 3.2vw, 2.6rem)", lineHeight: 1 }}
          >
            <span className="gradient-text">A modern performance ritual.</span>
          </p>
        </div>
      </div>
    </section>
  );
}

function Layer({ l, i }) {
  const ref = useReveal();
  const { Diagram } = l;
  return (
    <div
      ref={ref}
      data-testid={`beyond-layer-${i}`}
      className="reveal bg-[#050608] p-7 lg:p-9 hover:bg-[#070b13] transition-colors duration-500 group relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(31,179,255,0.45), transparent)",
        }}
      />

      {/* Top row: index label + animated diagram */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3 flex-1">
          <span
            className="font-mono text-[10px] uppercase tracking-[0.3em]"
            style={{ color: "var(--blue-bright)" }}
          >
            / {l.n}
          </span>
          <span className="h-px flex-1 bg-white/8" />
        </div>

        {Diagram && (
          <div
            data-testid={`beyond-diagram-${i}`}
            aria-hidden
            className="w-[88px] h-[58px] shrink-0 opacity-55 group-hover:opacity-100 transition-opacity duration-500"
            style={{ filter: "drop-shadow(0 0 8px rgba(31,179,255,0.35))" }}
          >
            <Diagram />
          </div>
        )}
      </div>

      <h3 className="font-display text-2xl lg:text-3xl uppercase text-white leading-tight">
        {l.title}
      </h3>
      <p className="mt-3 text-white/55 text-sm lg:text-base leading-relaxed max-w-xs">
        {l.body}
      </p>
    </div>
  );
}
